import { useState, useEffect, useCallback } from 'react';
// @mui
import {
  // Tooltip,
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  AvatarGroup,
  Avatar,
} from '@mui/material';
import { toast } from 'react-toastify';
// components
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';

// styles
import 'react-toastify/dist/ReactToastify.css';
import {
  getTeam,
  createTeam,
  updateTeam,
  deleteTeam,
  getAllManagers,
  getAllEmployee,
} from '../../../services/teamService';
import { ActionsPopover, DeleteModal, EmptyTableBody, Header, TeamFormModal, TeamListHead, TeamListToolbar } from '.';
import Loading from '../../../layouts/loading';
import { TABLE_HEAD } from '../constants';
import { DEFAULT_TABLE_PAGE_LIMIT, TABLE_PAGE_LIMIT_OPTIONS } from '../../../Constants/table.constants';
import BreadCrumb from '../../../components/breadcrumb/BreadCrumbNavigation';
// ----------------------------------------------------------------------

function TeamTable() {
  const [openActions, setOpenActions] = useState(null);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [action, setAction] = useState('');
  const [page, setPage] = useState(0);
  const [selectedRow, setSelectedRow] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_TABLE_PAGE_LIMIT);
  const [totalCount, setTotalCount] = useState([]);
  const [teamList, setTeamList] = useState([]);
  const [allManagerList, setAllManagerList] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [isLoading, setLoading] = useState(false);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const handleCreateClick = () => {
    setAction('create');
    setOpenFormModal(true);
  };

  const handleFormModalClose = useCallback(() => {
    setOpenFormModal(false);
  }, []);

  const handleOpenMenu = (event, row) => {
    setOpenActions(event.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setOpenActions(null);
  };

  const handleDeleteModalClose = () => setOpenDelete(false);

  const handleDeleteClick = useCallback(() => {
    setOpenActions(null);
    setOpenDelete(true);
  }, []);

  const handleEditClick = () => {
    setOpenActions(null);
    setAction('update');
    setOpenFormModal(true);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - totalCount) : 0;
  const isNotFound = !totalCount && !!filterName;

  const fetchTeamData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getTeam(page, rowsPerPage, filterName);
      setTotalCount(response?.data?.data?.count);
      setTeamList(response?.data?.data?.rows);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filterName]);

  const handleDelete = async () => {
    try {
      const Team = await deleteTeam(selectedRow?.id);
      toast.success(Team?.data?.message);
      handleDeleteModalClose();
      fetchTeamData();
    } catch (error) {
      toast.error('Error while deleting');
      console.error(error);
    }
  };
  const handleFormModalSubmit = async (values) => {
    try {
      let response;
      const teammenbers = values?.team?.map((member) => ({ userId: member?.id })) ?? [];
      if (action === 'create') {
        response = await createTeam({ managerId: values.managerId, team: teammenbers });
      } else if (action === 'update') {
        response = await updateTeam(selectedRow?.id, { managerId: values.managerId, team: teammenbers });
      }
      fetchTeamData();
      toast.success(response?.data?.message);
      setOpenFormModal(false);
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  useEffect(() => {
    const fetchManagerData = async () => {
      const response = await getAllManagers();
      const allMangers = response.data?.data?.rows ?? [];
      setAllManagerList(allMangers);
    };

    const fetchEmployeeData = async () => {
      const response = await getAllEmployee();

      const list = response?.data?.data?.rows.map((row) => ({ label: row.userName, id: row.id }));

      const seenIds = new Set();
      const uniqueEmployees = list.filter((item) => {
        if (!seenIds.has(item.id)) {
          seenIds.add(item.id);
          return true;
        }
        return false;
      });
      setEmployeeList(uniqueEmployees);
    };

    fetchManagerData();
    fetchEmployeeData();
  }, []);

  const breadcrumbs = [
    {
      label: 'Home',
      path: '/',
    },
    {
      label: 'Team',
      path: '',
    },
  ];
  return (
    <>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Container>
        <Loading open={isLoading} />

        <Header onCreateClick={handleCreateClick} />

        <Card>
          <TeamListToolbar
            numSelected={selectedRow?.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
          />
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <TeamListHead headLabel={TABLE_HEAD} />
                <TableBody>
                  {teamList.map((row) => {
                    const { id, users, teammembers } = row;
                    return (
                      <TableRow hover key={id} tabIndex={-1} role="checkbox">
                        <TableCell component="th" scope="row">
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography variant="subtitle2" noWrap>
                              <span style={{ textTransform: 'capitalize' }}>{users?.userName}</span>
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell align="left">
                          <AvatarGroup max={4} sx={{ float: 'left' }}>
                            {teammembers?.map((member) => (
                              // <Tooltip key={member?.id} title={member?.user?.userName} arrow>
                              <Avatar key={member?.id}>{member?.user?.userName?.[0]}</Avatar>
                              // </Tooltip>
                            ))}
                          </AvatarGroup>
                        </TableCell>

                        <TableCell align="right">
                          <IconButton size="large" color="inherit" onClick={(event) => handleOpenMenu(event, row)}>
                            <Iconify icon={'eva:more-vertical-fill'} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && <EmptyTableBody filterName={filterName} />}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={TABLE_PAGE_LIMIT_OPTIONS}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>

      <ActionsPopover
        open={openActions}
        onClose={handleCloseMenu}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteClick}
      />

      {openFormModal && (
        <TeamFormModal
          open
          onClose={handleFormModalClose}
          selectedRow={selectedRow}
          onSubmit={handleFormModalSubmit}
          action={action}
          allManagerList={allManagerList}
          employeeList={employeeList}
        />
      )}

      <DeleteModal open={openDelete} onClose={handleDeleteModalClose} onDelete={handleDelete} />
    </>
  );
}

export default TeamTable;
